<template>
  <div
    class="c-base-date"
    :class="{ 'c-base-date--reflowed': isReflowed }"
    data-testid="base-date"
  >
    <div class="c-base-date__body">
      <div
        v-if="rangeOptionsOnly || (showRangeOptions && !isReflowed)"
        class="c-base-date__range-options"
        :class="{ 'c-base-date__range-options--grid': rangeOptionsOnly }"
        data-testid="base-date-range-options"
      >
        <template
          v-for="(option, optionKey) in localRangeOptions"
          :key="optionKey"
        >
          <BaseButton
            :active="dateRangeLabel === option.rangeLabel"
            active-color="secondary"
            :aria-current="
              dateRangeLabel === option.rangeLabel ? 'true' : undefined
            "
            color="white"
            :data-testid="`range-option-${option.rangeLabel}`"
            :dense="rangeOptionsOnly"
            :disabled="disabled"
            :label="option.localRangeLabel"
            :menu-item="!rangeOptionsOnly"
            new-design
            new-icon
            size="md"
            @click="setDateRange(option)"
          />
        </template>
      </div>

      <div
        v-if="!rangeOptionsOnly"
        class="c-base-date__main"
      >
        <template v-if="showDateInput">
          <div class="c-base-date__selections">
            <div
              v-if="rangeSelection !== 'day'"
              class="c-base-date__selection"
            >
              <div class="c-base-date__selection-title">
                {{ $t("input_date.from_title") }}
              </div>
              <div class="c-base-date__selection-inputs">
                <BaseInputText
                  v-model="startDateDay"
                  class="c-base-date__selection-inputs-day"
                  :disabled="disabled"
                  new-design
                  @keydown.enter.prevent
                />
                <BaseInputTimePicker
                  v-if="showTimeInput"
                  v-model="startDateTime"
                  :action-button-label="actionButtonLabel"
                  class="c-base-date__selection-inputs-time"
                  :disabled="disabled"
                  :max-time="lastDate"
                  :min-time="firstDate"
                  :minute-step="minuteStep"
                  :show-seconds="showSeconds"
                  @keydown.enter.prevent
                />
              </div>
            </div>
            <div class="c-base-date__selection">
              <div
                v-if="rangeSelection !== 'day'"
                class="c-base-date__selection-title"
              >
                {{ $t("input_date.to_title") }}
              </div>
              <div class="c-base-date__selection-inputs">
                <BaseInputText
                  v-model="endDateDay"
                  class="c-base-date__selection-inputs-day"
                  :disabled="disabled"
                  new-design
                  @keydown.enter.prevent
                />
                <BaseInputTimePicker
                  v-if="showTimeInput"
                  v-model="endDateTime"
                  :action-button-label="actionButtonLabel"
                  class="c-base-date__selection-inputs-time"
                  :disabled="disabled"
                  :max-time="lastDate"
                  :min-time="firstDate"
                  :minute-step="minuteStep"
                  :show-seconds="showSeconds"
                  @keydown.enter.prevent
                />
              </div>
            </div>
          </div>
          <BaseSeparator />
        </template>

        <div class="c-base-date__calendars">
          <div
            v-if="showPreviousIntervals"
            class="c-base-date__calendar"
          >
            <div class="c-base-date__month">
              <BaseButton
                circle
                :disabled="!canShowPreviousIntervals || disabled"
                icon="fas fa-chevron-left"
                icon-size="xs"
                new-design
                size="sm"
                @click="gotoPrevious"
              />
              <div>
                <BaseButton
                  :aria-label="$t('input_date.previous_interval_label')"
                  circle
                  :disabled="disabled"
                  :label="previousIntervalLabel"
                  new-design
                  @click="setInterval"
                />
              </div>
              <div />
            </div>
            <div
              v-if="currentInterval !== 'month'"
              class="c-base-date__week"
            >
              <div>{{ $t("input_date.day.sunday") }}</div>
              <div>{{ $t("input_date.day.monday") }}</div>
              <div>{{ $t("input_date.day.tuesday") }}</div>
              <div>{{ $t("input_date.day.wednesday") }}</div>
              <div>{{ $t("input_date.day.thursday") }}</div>
              <div>{{ $t("input_date.day.friday") }}</div>
              <div>{{ $t("input_date.day.saturday") }}</div>
            </div>
            <div
              class="c-base-date__intervals"
              :class="`c-base-date__intervals-${currentInterval}`"
            >
              <template
                v-for="(day, dayIndex) in previousDateIntervals"
                :key="`day_${dayIndex}`"
              >
                <div
                  :aria-label="day.label"
                  class="c-base-date__intervals-interval"
                  :class="{
                    range: day.isWithinRange,
                    outside: !day.isCurrent,
                    active: day.active,
                    start: day.start,
                    end: day.end,
                    disable: day.disabled || disabled,
                    pending: day.pending,
                  }"
                  @click="disabled ? null : setDate(day.date, day.disabled)"
                  @mouseenter="setPendingDate(day)"
                  @mouseleave="setPendingDate(null)"
                >
                  <span>
                    {{ day.label }}
                  </span>
                </div>
              </template>
            </div>
          </div>

          <div class="c-base-date__calendar">
            <div class="c-base-date__month">
              <BaseButton
                v-if="!showPreviousIntervals"
                :aria-label="$t('input_date.previous_label')"
                circle
                :disabled="!canShowPreviousIntervals || disabled"
                icon="fas fa-chevron-left"
                icon-size="xs"
                new-design
                size="sm"
                @click="gotoPrevious"
              />
              <div v-else />
              <div>
                <BaseButton
                  :aria-label="
                    $t('input_date.interval_label_with_value', {
                      interval: intervalLabel,
                    })
                  "
                  circle
                  data-testid="base-date-interval"
                  :disabled="disabled"
                  :label="intervalLabel"
                  new-design
                  @click="setInterval"
                />
              </div>
              <BaseButton
                :aria-label="$t('input_date.next_label')"
                circle
                :disabled="!canShowNextIntervals || disabled"
                icon="fas fa-chevron-right"
                icon-size="xs"
                new-design
                size="sm"
                @click="gotoNext"
              />
            </div>
            <div
              v-if="currentInterval !== 'month'"
              class="c-base-date__week"
            >
              <div>{{ $t("input_date.day.sunday") }}</div>
              <div>{{ $t("input_date.day.monday") }}</div>
              <div>{{ $t("input_date.day.tuesday") }}</div>
              <div>{{ $t("input_date.day.wednesday") }}</div>
              <div>{{ $t("input_date.day.thursday") }}</div>
              <div>{{ $t("input_date.day.friday") }}</div>
              <div>{{ $t("input_date.day.saturday") }}</div>
            </div>
            <div
              class="c-base-date__intervals"
              :class="`c-base-date__intervals-${currentInterval}`"
            >
              <template
                v-for="(day, dayIndex) in dateIntervals"
                :key="`day_${dayIndex}`"
              >
                <div
                  :aria-label="day.label"
                  class="c-base-date__intervals-interval"
                  :class="{
                    range: day.isWithinRange,
                    outside: !day.isCurrent,
                    active: day.active,
                    start: day.start,
                    end: day.end,
                    disable: day.disabled || disabled,
                    pending: day.pending,
                  }"
                  @click="disabled ? null : setDate(day.date, day.disabled)"
                  @mouseenter="setPendingDate(day)"
                  @mouseleave="setPendingDate(null)"
                >
                  <span>
                    {{ day.label }}
                  </span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <slot
      name="afterBody"
      :set-custom-date="setCustomDate"
    />

    <template v-if="showUpdateButton">
      <BaseSeparator />

      <div class="c-base-date__actions">
        <div
          v-if="!hideUserTimeZone"
          class="c-base-date__info"
        >
          {{ currentUserTimezone }}
        </div>
        <div class="c-base-date__actions-buttons c-base-date__actions-slot">
          <slot
            name="action"
            :set-custom-date="setCustomDate"
          />
        </div>
        <div class="c-base-date__actions-buttons c-base-date__actions-update">
          <BaseButton
            :disabled="isSelecting || disabled"
            :label="$t('global.cancel')"
            new-design
            @click="$emit('cancel')"
          />
          <BaseButton
            color="primary"
            :disabled="isSelecting || disabled"
            :label="actionButtonLabel"
            new-design
            @click="updateRange"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import type {
  CalendarEmit,
  CalendarModel,
  CalendarProps,
} from "shared/composables/useCalendar";
import useCalendar from "shared/composables/useCalendar";
import DateRange from "shared/helpers/DateRange";

import BaseButton from "../BaseButton";
import BaseInputText from "../BaseInputText.vue";
import BaseInputTimePicker from "../BaseInputTimePicker";
import BaseSeparator from "../BaseSeparator";

import { useInputDateReflow } from "./useInputDateReflow";

const model = defineModel<CalendarModel>({ default: null });

const props = withDefaults(defineProps<CalendarProps>(), {
  actionButtonLabel: () => getLocaleText("input_date.default_button_label"),
  intervalView: "day",
  minuteStep: 1,
  rangeOptions: () => [],
  rangeSelection: "day",
});

const emit = defineEmits<CalendarEmit>();

const { isReflowed } = useInputDateReflow();

const {
  canShowNextIntervals,
  canShowPreviousIntervals,
  currentInterval,
  currentUserTimezone,
  dateIntervals,
  dateRangeLabel,
  endDateDay,
  endDateTime,
  firstDate,
  intervalLabel,
  isSelecting,
  lastDate,
  previousDateIntervals,
  previousIntervalLabel,
  startDateDay,
  startDateTime,

  gotoNext,
  gotoPrevious,
  setCustomDate,
  setDate,
  setDateRange,
  setInterval,
  setPendingDate,
  updateRange,
} = useCalendar(model, props, { emit });

const localRangeOptions = computed<DateRange[]>(() => {
  if (props.rangeOptions?.length) {
    return props.rangeOptions;
  }

  const options = [
    DateRange.today(),
    DateRange.yesterday(),
    DateRange.lastThreeDays(),
    DateRange.lastSevenDays(),
    DateRange.lastFourteenDays(),
    DateRange.lastThirtyDays(),
    DateRange.lastNinetyDays(),
    DateRange.currentMonth(),
    DateRange.lastMonth(),
    DateRange.lastQuarter(),
  ];

  if (props.allowAllTime || props.allowLastYearShortcut) {
    options.push(DateRange.lastYear());
  }

  return options;
});
</script>

<style lang="scss" scoped>
.c-base-date {
  --interval-bg: transparent;
  --interval-label-bg: transparent;
  --interval-label-bg-hover: var(--s-button-bg-secondary-default);
  --interval-label-color: var(--s-button-color-white-default);
  --interval-label-color-hover: var(--s-button-color-white-hover);
  --interval-label-width: auto;
  --info-border: 1px solid var(--s-color-denim-2);

  display: flex;
  flex-direction: column;
  font-size: var(--s-font-size-md);

  // The popper publishes the room it has. Without this the panel keeps its
  // natural height and spills off the viewport instead of scrolling inside.
  max-height: var(--date-max-height, var(--popper-available-height, none));
  overflow-y: auto;

  &__body {
    display: flex;
  }

  &__info {
    display: flex;
    align-items: center;
    font-size: var(--s-font-size-sm);
    grid-area: timezone;
    border-top: var(--info-border);
    padding-block: var(--s-spacing-sm);
    padding-left: var(--s-spacing-lg);
  }

  &__actions {
    display: grid;
    grid-template: auto 1fr / auto;
    grid-template-areas:
      "slot update"
      "timezone timezone";

    &-buttons {
      display: flex;
      gap: var(--s-spacing-md);
      padding: var(--s-spacing-md);
    }

    &-slot {
      grid-area: slot;

      &:empty {
        display: none;
      }
    }

    &-update {
      grid-area: update;
      justify-content: flex-end;
    }

    &:has(.c-base-date__actions-slot:empty) {
      --info-border: none;

      grid-template-areas: "timezone update";
    }
  }

  &__range-options {
    border-right: 1px solid var(--s-color-denim-2);
    padding: var(--s-spacing-md);
    display: flex;
    flex-direction: column;
    max-height: 400px;
    overflow-y: auto;

    &--grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      border-right: none;
      padding: 0;
      flex: 1;
      gap: var(--s-spacing-md);
    }
  }

  &__selections {
    display: flex;
    gap: var(--s-spacing-lg);
    padding-block: var(--s-spacing-md);
    padding-inline: var(--s-spacing-lg);
  }

  &__selection {
    display: flex;
    flex-direction: column;
    gap: var(--s-spacing-sm);
    flex: 1;

    &-title {
      font-weight: var(--s-font-weight-bold);
    }

    &-inputs {
      display: flex;
      align-items: center;
      gap: var(--s-spacing-sm);

      &-day {
        max-width: 100px;
      }

      &-time {
        max-width: 80px;
      }
    }

    &-label {
      span {
        &:nth-child(2) {
          color: var(--s-color-font-subtext);
        }
      }
    }
  }

  &__calendars {
    display: flex;
    gap: var(--s-spacing-lg);
    padding-block: var(--s-spacing-md);
    padding-inline: var(--s-spacing-lg);
  }

  &__calendar {
    display: flex;
    flex-direction: column;
    gap: var(--s-spacing-md);
    flex: 1;
    min-width: 256px;
  }

  &__month {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-block: var(--s-spacing-md);
  }

  &__week {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: var(--s-spacing-sm);

    > * {
      text-align: center;
    }
  }

  &__intervals {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: var(--s-spacing-md) 0;

    &-month {
      --interval-label-width: 100%;

      grid-template-columns: repeat(3, 1fr);
    }

    &-interval {
      background-color: var(--interval-bg);
      display: flex;
      justify-content: center;
      position: relative;
      z-index: 1;
      cursor: pointer;

      &.range {
        --interval-bg: var(--s-button-bg-secondary-default);

        &:hover {
          --interval-label-bg-hover: var(--s-button-bg-secondary-hover);
        }
      }

      &.active {
        --interval-bg: transparent;
        --interval-label-bg: var(--s-button-bg-primary-default);
        --interval-label-bg-hover: var(--s-button-bg-primary-hover) !important;
        --interval-label-color: var(
          --s-button-color-primary-default
        ) !important;
        --interval-label-color-hover: var(--s-button-color-primary-hover);
      }

      &.pending {
        --interval-label-bg: var(--s-button-bg-primary-default);
        --interval-label-bg-hover: var(--s-button-bg-primary-default);
        --interval-label-color: var(
          --s-button-color-primary-default
        ) !important;
      }

      &.disable {
        cursor: not-allowed;

        --interval-label-color: var(--s-color-denim-5);
        --interval-label-bg-hover: transparent;
      }

      &.outside {
        --interval-label-color: var(--s-color-denim-5);
      }

      &.start,
      &.end {
        --interval-bg: transparent;

        &::before {
          content: "";
          position: absolute;
          z-index: 1;
          inset: 0 50% 0 0;
          background: var(--s-color-denim-2);
        }
      }

      &.start {
        &::before {
          right: 0;
          left: 50%;
        }
      }

      &:hover {
        --interval-label-bg: var(--interval-label-bg-hover);
      }

      span {
        width: var(--interval-label-width);
        background-color: var(--interval-label-bg);
        color: var(--interval-label-color);
        height: var(--s-size-controls-sm);
        min-width: var(--s-size-controls-sm);
        border-radius: var(--s-border-radius-full);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        position: relative;
        z-index: 10;
        transition: 150ms background-color;
      }
    }
  }
}

// Rows fold into columns and the presets drop out, taking the panel from ~555px
// to ~300px — inside the 320px floor WCAG 1.4.10 Reflow asks for.
.c-base-date--reflowed {
  // Pinned to the viewport, so its room is the screen, not the popper's offer.
  --date-max-height: 100dvh;

  width: 100%;

  .c-base-date__body {
    flex-direction: column;
  }

  // Only reachable in `rangeOptionsOnly`, where the presets are the whole
  // panel, so they take the full width rather than a column beside a calendar.
  .c-base-date__range-options {
    border-right: none;
    flex-flow: row wrap;

    // As the whole panel they belong to its scroll; a second one traps them.
    max-height: none;
    overflow-y: visible;
  }

  .c-base-date__selections {
    flex-direction: column;
  }

  .c-base-date__calendars {
    flex-direction: column;
  }

  .c-base-date__calendar {
    // The floor holds two calendars side by side. Stacked there is only one,
    // and seven 26px columns fit well inside 320px.
    min-width: 0;
  }

  .c-base-date__actions {
    grid-template: auto auto auto / auto;
    grid-template-areas:
      "slot"
      "update"
      "timezone";

    &:has(.c-base-date__actions-slot:empty) {
      grid-template-areas:
        "update"
        "timezone";
    }
  }

  .c-base-date__info {
    // Reads as a footnote under the buttons rather than a column beside them.
    padding-inline: var(--s-spacing-lg);
  }
}
</style>
